import { IntlShape } from 'react-intl'

import { ISelectProposalForm } from '../../modal-select-proposal.types'
import {
  ClientPricingItemTypeEnum,
  ClientPricingMenuOutputDto,
  ClientPricingMenuTypeEnum,
  CurrencyEnum,
} from '../../../../../lib/graphql'
import {
  calcNights,
  provisionUnitToLabel,
  roundedPrice,
} from '../../../../../utils'
import { VAT_STATUS } from '../../../../contexts'

export const getLogsMenuItemName = (
  menu: ClientPricingMenuOutputDto,
): string => {
  switch (menu.type) {
    case ClientPricingMenuTypeEnum.HOUSE_MENU:
      return 'Location sèche'
    case ClientPricingMenuTypeEnum.DAY_MENU:
      return 'Formule journée avec restauration'
    case ClientPricingMenuTypeEnum.ACTIVITY_MENU:
      return 'Activité'
    case ClientPricingMenuTypeEnum.NIGHT_MENU: {
      const hasIndividualBedroomItem = menu.nestedItems.some(
        (item) => item.type === ClientPricingItemTypeEnum.INDIVIDUAL_BEDROOMS,
      )
      const hasSharedBedroomItem = menu.nestedItems.some(
        (item) => item.type === ClientPricingItemTypeEnum.SHARED_BEDROOMS,
      )
      return `Formule pension complète ${hasIndividualBedroomItem ? ' single' : ''}${
        hasSharedBedroomItem ? ' twin' : ''
      }`
    }
    case ClientPricingMenuTypeEnum.EVENING_MENU:
      return 'Pack soirée'
    case ClientPricingMenuTypeEnum.WORKSPACE_MENU:
      return 'Espace de travail'
  }
}

export const getMenuItemName = ({
  menu,
  intl,
}: {
  menu: ClientPricingMenuOutputDto
  intl: IntlShape
}): string => {
  switch (menu.type) {
    case ClientPricingMenuTypeEnum.HOUSE_MENU:
      return intl.formatMessage({
        id: 'ui.getMenuItemName.houseMenu',
        defaultMessage: 'Location sèche',
      })
    case ClientPricingMenuTypeEnum.DAY_MENU:
      return intl.formatMessage({
        id: 'ui.getMenuItemName.dayMenu',
        defaultMessage: 'Formule journée avec restauration',
      })
    case ClientPricingMenuTypeEnum.ACTIVITY_MENU:
      return intl.formatMessage({
        id: 'ui.getMenuItemName.activityMenu',
        defaultMessage: 'Activité',
      })
    case ClientPricingMenuTypeEnum.NIGHT_MENU: {
      const hasIndividualBedroomItem = menu.nestedItems.some(
        (item) => item.type === ClientPricingItemTypeEnum.INDIVIDUAL_BEDROOMS,
      )
      const hasSharedBedroomItem = menu.nestedItems.some(
        (item) => item.type === ClientPricingItemTypeEnum.SHARED_BEDROOMS,
      )
      return intl.formatMessage(
        {
          id: 'ui.getMenuItemName.nightMenu',
          defaultMessage: 'Formule pension complète {single}{twin}',
        },
        {
          single: hasIndividualBedroomItem
            ? intl.formatMessage({
                id: 'ui.getMenuItemName.single',
                defaultMessage: ' single',
              })
            : '',
          twin: hasSharedBedroomItem
            ? intl.formatMessage({
                id: 'ui.getMenuItemName.twin',
                defaultMessage: ' twin',
              })
            : '',
        },
      )
    }
    case ClientPricingMenuTypeEnum.EVENING_MENU:
      return intl.formatMessage({
        id: 'ui.getMenuItemName.eveningMenu',
        defaultMessage: 'Pack soirée',
      })
    case ClientPricingMenuTypeEnum.WORKSPACE_MENU:
      return intl.formatMessage({
        id: 'ui.getMenuItemName.workspaceMenu',
        defaultMessage: 'Espace de travail',
      })
  }
}

export const getMenuItemPrice = ({
  menu,
  vatStatus,
  currency,
  intl,
}: {
  menu: Pick<ClientPricingMenuOutputDto, 'unitMenu' | 'unit'>
  vatStatus: VAT_STATUS
  currency?: CurrencyEnum
  intl: IntlShape
}): string => {
  const priceValue =
    vatStatus === VAT_STATUS.HT ? 'priceWithoutVat' : 'priceWithVat'

  const price = menu.unitMenu.totalUser?.priceWithDiscountAndFees

  if (!price) {
    throw new Error('Price is not defined')
  }

  return `${roundedPrice(price[priceValue], { currency })}${provisionUnitToLabel(
    {
      unit: menu.unit,
      fullLabel: true,
      intl,
    },
  )}`
}

export const getMenuItemMaxQuantity = ({
  menu,
  formValues,
}: {
  menu: Pick<ClientPricingMenuOutputDto, 'type'>
  formValues: Pick<ISelectProposalForm, 'dates'>
}): number | undefined => {
  if (
    menu.type === ClientPricingMenuTypeEnum.EVENING_MENU &&
    formValues.dates.startDate &&
    formValues.dates.endDate
  ) {
    return calcNights(formValues.dates.startDate, formValues.dates.endDate)
  }

  return undefined
}
